import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Bell, Receipt, Coffee, MessageSquarePlus, CheckCircle2, Inbox, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

const TYPE_META = {
  waiter: { icon: Bell, color: "purple", label: { de: "Kellner gerufen", en: "Waiter called", tr: "Garson çağırıldı" } },
  bill: { icon: Receipt, color: "emerald", label: { de: "Rechnung angefordert", en: "Bill requested", tr: "Hesap istendi" } },
  water: { icon: Coffee, color: "blue", label: { de: "Getränk angefordert", en: "Drink requested", tr: "İçecek istendi" } },
  custom: { icon: MessageSquarePlus, color: "amber", label: { de: "Nachricht", en: "Message", tr: "Mesaj" } },
};

function timeAgo(iso, lang) {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return lang === "de" ? "gerade eben" : lang === "tr" ? "az önce" : "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return lang === "de" ? `vor ${m} Min` : lang === "tr" ? `${m} dk önce` : `${m} min ago`;
  }
  const h = Math.floor(diff / 3600);
  return lang === "de" ? `vor ${h} Std` : lang === "tr" ? `${h} sa önce` : `${h} hr ago`;
}

export default function ServiceRequests() {
  const { id } = useParams();
  const { lang } = useLang();
  const [requests, setRequests] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const lastCountRef = useRef(0);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/restaurants/${id}/service-requests`, {
        params: { status: showResolved ? "resolved" : "open" },
      });
      // Play sound if a new open request arrived
      if (!showResolved && data.length > lastCountRef.current && lastCountRef.current > 0) {
        try { audioRef.current?.play().catch(() => {}); } catch {}
      }
      lastCountRef.current = data.length;
      setRequests(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // poll every 5s
    return () => clearInterval(t);
  }, [id, showResolved]);

  const resolve = async (rqId) => {
    try {
      await api.patch(`/service-requests/${rqId}`);
      toast.success(lang === "de" ? "Erledigt" : lang === "tr" ? "Tamamlandı" : "Done");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const title = lang === "de" ? "Tisch-Anfragen" : lang === "tr" ? "Masa Çağrıları" : "Table requests";

  return (
    <DashboardLayout restaurantId={id}>
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=" type="audio/wav" />
      </audio>
      <div className="space-y-6">
        {/* In-Bearbeitung warning banner */}
        <div className="rounded-2xl border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-start gap-3" data-testid="telegram-cta-banner">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 grid place-items-center shrink-0">
            <Send className="h-4 w-4" />
          </div>
          <div className="flex-1 text-sm">
            <div className="font-bold text-emerald-700 dark:text-emerald-300">
              {lang === "de" ? "Live-Push aufs Handy aktivieren" : lang === "tr" ? "Telefona canlı bildirim aktif et" : "Enable live push to phone"}
            </div>
            <p className="mt-1 text-emerald-700/80 dark:text-emerald-200/80 leading-relaxed">
              {lang === "de"
                ? "Verbinde Telegram in 30 Sekunden — diese Seite muss dann nicht mehr offen bleiben."
                : lang === "tr"
                ? "30 saniyede Telegram'a bağlan — bu sayfanın açık kalmasına gerek kalmaz."
                : "Connect Telegram in 30 seconds — you won't need to keep this page open anymore."}
            </p>
            <Link to={`/dashboard/restaurants/${id}/telegram`}>
              <Button size="sm" className="mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="telegram-cta-btn">
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {lang === "de" ? "Telegram verbinden" : lang === "tr" ? "Telegram'ı bağla" : "Connect Telegram"}
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {lang === "de" ? "Live-Tisch-Service" : lang === "tr" ? "Canlı Masa Servisi" : "Live table service"}
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {lang === "de"
                ? "Gäste rufen den Service direkt aus dem Menü. Du siehst hier in Echtzeit, wer was braucht."
                : lang === "tr"
                ? "Müşteriler menüden direkt servis çağırır. Burada kimin neye ihtiyacı olduğunu canlı görürsün."
                : "Guests call service straight from the menu. See who needs what — in real time."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={showResolved ? "outline" : "default"}
              className={`rounded-full ${!showResolved ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
              onClick={() => setShowResolved(false)}
              data-testid="filter-open"
            >
              {lang === "de" ? "Offen" : lang === "tr" ? "Aktif" : "Open"} ({!showResolved ? requests.length : "·"})
            </Button>
            <Button
              size="sm"
              variant={showResolved ? "default" : "outline"}
              className={`rounded-full ${showResolved ? "bg-zinc-700 hover:bg-zinc-800 text-white" : ""}`}
              onClick={() => setShowResolved(true)}
              data-testid="filter-resolved"
            >
              {lang === "de" ? "Erledigt" : lang === "tr" ? "Geçmiş" : "Resolved"}
            </Button>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="rounded-2xl border bg-card p-10 text-center">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              {showResolved
                ? lang === "de" ? "Noch keine erledigten Anfragen" : lang === "tr" ? "Henüz tamamlanan çağrı yok" : "No resolved requests yet"
                : lang === "de" ? "Keine offenen Anfragen. Wenn jemand den Service ruft, erscheint es hier." : lang === "tr" ? "Aktif çağrı yok. Biri servis çağırırsa burada belirir." : "No open requests. Calls appear here in real time."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {requests.map((rq) => {
              const meta = TYPE_META[rq.type] || TYPE_META.custom;
              const Icon = meta.icon;
              return (
                <div
                  key={rq.id}
                  className={`rounded-2xl border bg-card p-4 ${rq.status === "open" ? `border-${meta.color}-500/40 shadow-${meta.color}-500/10` : "opacity-70"}`}
                  data-testid={`request-${rq.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-${meta.color}-500/15 text-${meta.color}-600 dark:text-${meta.color}-400`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-sm">{meta.label[lang] || meta.label.de}</div>
                        {rq.table_label ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-500/15 font-semibold">{rq.table_label}</span>
                        ) : rq.table_id ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-500/15 font-mono">#{rq.table_id}</span>
                        ) : null}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(rq.created_at, lang)}</div>
                      {rq.note && <div className="text-xs mt-2 p-2 rounded-lg bg-muted/40 border italic">"{rq.note}"</div>}
                      {rq.status === "open" && (
                        <Button
                          size="sm"
                          className="w-full mt-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => resolve(rq.id)}
                          data-testid={`resolve-${rq.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                          {lang === "de" ? "Erledigt" : lang === "tr" ? "Tamamlandı" : "Mark done"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
